import { useState, useCallback } from 'react';
import { WmtTaskNote } from './taskNotes.types';

/**
 * Module-level cache: persists notes across modal open/close cycles within the same page session.
 *
 * When the backend is ready, replace this cache with API calls inside this hook.
 * The consuming components (`EmailTaskModal`, `MissingEmailTaskModal`, `TaskNotesSection`)
 * will require zero changes.
 */
const notesCache = new Map<string, WmtTaskNote[]>();

/**
 * Derives up to 2 initials from a full name.
 * "John Smith" → "JS", "Alice" → "A", "" → "?"
 */
function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Manages WMT task notes for a specific `groupId`.
 *
 * Notes are stored in a module-level Map, so they survive modal open/close
 * as long as the page session is active.
 *
 * @param groupId - The WMT task's `groupId` to scope notes to.
 */
export function useTaskNotes(groupId: string) {
  const [notes, setNotes] = useState<WmtTaskNote[]>(() => notesCache.get(groupId) ?? []);

  /**
   * Adds a new note to the conversation for this task.
   * Updates both the module-level cache and local React state.
   *
   * @param text - The note text (should be trimmed before calling).
   * @param authorName - Full display name of the current user.
   * @returns The newly created `WmtTaskNote`.
   */
  const addNote = useCallback(
    (text: string, authorName: string): WmtTaskNote => {
      const note: WmtTaskNote = {
        id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        groupId,
        text: text.trim(),
        authorName,
        authorInitials: getInitials(authorName),
        timestamp: new Date(),
      };
      const updated = [...(notesCache.get(groupId) ?? []), note];
      notesCache.set(groupId, updated);
      setNotes(updated);
      return note;
    },
    [groupId]
  );

  return { notes, addNote };
}
